import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import { controlApiOrigin } from '@/services/control/control-origin';
import { controlStaffService } from '@/services/control/staff-service';
import type { StaffIdentity } from '@/types/control/staff';

import { adminQueryKeys } from './query-keys';
import { retryControlApi } from './retry';

/**
 * The current user's staff standing, read from control-api.
 *
 * Staff tier is resolved server-side on every request, so a revoked grant is
 * denied on the next call rather than living on in a token. The cached value
 * here only drives what the UI offers; control-api enforces independently, so a
 * stale cache cannot widen access.
 *
 * `enabled` defaults to false so that adding this hook somewhere cannot start a
 * cross-origin request by accident. The app layout passes `ADMIN_AREA_ENABLED`;
 * the admin area's own gate passes `true`, because a user who navigated to
 * `/admin` deliberately does need the answer.
 */
export function useStaffIdentity({
  enabled = false,
}: { enabled?: boolean } = {}): UseQueryResult<StaffIdentity, Error> {
  return useQuery<StaffIdentity, Error>({
    queryKey: adminQueryKeys.staffIdentity(),
    queryFn: ({ signal }) => controlStaffService.getIdentity(signal),
    // Without a derivable origin the queryFn can only throw, which on localhost
    // leaves a permanently failed query in every developer's devtools.
    enabled: enabled && Boolean(controlApiOrigin()),
    staleTime: 60_000,
    // main.tsx sets refetchOnMount and refetchOnWindowFocus to 'always'
    // globally, and both override staleTime — so without these the 60s above
    // does nothing and this refires on every mount and every tab focus. Staff
    // tier does not change mid-session, and a revoked grant is refused
    // server-side on the next admin request regardless.
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: retryControlApi,
  });
}
